// ── What each firm publishes about itself: phone, email, website (multi-tenant, firmcontact) ──────
// Read from the server (/api/firm-contact/public = the companies rows, Admin → Companies) once per page
// load and shared by every component; nothing in the client spells a firm's phone, mailbox or site.
// null until loaded (or when the server has none) — callers then print no phone / no link.
import { useEffect, useState } from 'react';
import { firmContactApi, FirmContactCard, FirmContacts } from './api/firmcontact';
import { api } from './api';

export type { FirmContactCard, FirmContacts };
export type FirmKind = 'legal' | 'accounting';

let cache: FirmContacts | null = null;
let pending: Promise<FirmContacts | null> | null = null;
const listeners = new Set<(c: FirmContacts) => void>();

export function loadFirmContacts(): Promise<FirmContacts | null> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = firmContactApi.publicContacts()
      .then(c => { cache = c; listeners.forEach(l => l(c)); return c; })
      .catch(() => { pending = null; return null; });   // retried by the next component that asks
  }
  return pending;
}
/** The contacts already loaded (null before the first answer) — for code outside React's render. */
export const firmContactsNow = (): FirmContacts | null => cache;

/** Every firm kind's contact card; re-renders the caller once loaded. */
export function useFirmContacts(): FirmContacts | null {
  const [c, setC] = useState<FirmContacts | null>(cache);
  useEffect(() => {
    if (cache) { setC(cache); return undefined; }
    const l = (x: FirmContacts) => setC(x);
    listeners.add(l);
    void loadFirmContacts();
    return () => { listeners.delete(l); };
  }, []);
  return c;
}
export const useFirmContact = (kind: FirmKind): FirmContactCard | null => useFirmContacts()?.[kind] || null;

/** A page on the firm's website ('' when it has none — the caller then shows no link). */
export function firmSiteUrl(card: FirmContactCard | null | undefined, path = ''): string {
  const base = String(card?.website || '').replace(/\/+$/, '');
  if (!base) return '';
  const p = String(path || '');
  return base + (!p ? '' : p.startsWith('/') ? p : `/${p}`);
}

/** The legal firm's lease-registration quote share code (<site>/s/<code>) in free text, e.g. a job's
 *  comments — the site's host matched with or without "www.". null when absent or not loaded yet. */
export function quoteShareCode(text: any, legal: FirmContactCard | null | undefined = firmContactsNow()?.legal): string | null {
  const host = String(legal?.siteHost || '');
  if (!host) return null;
  const re = new RegExp(`${host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\/s\\/([a-z0-9-]{2,160})`, 'i');
  const m = re.exec(String(text || ''));
  return m ? m[1] : null;
}

// ── AI Marketing cities (workspace setting, server: services/firmContact marketCities) ─────────────
let citiesCache: string[] | null = null;
/** The cities AI Marketing prospects, in order (the first is the default); [] until loaded / when none is set. */
export function useMarketCities(): string[] {
  const [c, setC] = useState<string[]>(citiesCache || []);
  useEffect(() => {
    let alive = true;
    api.amDiscoverOptions().then((o: any) => {
      citiesCache = Array.isArray(o?.cities) ? o.cities.map((x: any) => String(x)) : [];
      if (alive) setC(citiesCache || []);
    }).catch(() => { /* the module's own list still shows the cities in the data */ });
    return () => { alive = false; };
  }, []);
  return c;
}
